/**
 * @fileoverview useSearchEntitlementConflicts - Custom hook for searching entitlement conflicts
 *
 * Provides a React hook interface for searching violating entitlements and managing
 * the associated loading states, data, and error handling. Wraps the RTK Query
 * mutation with additional state management for UI components.
 */

/* eslint-disable import/prefer-default-export */
import { useCallback, useState } from 'react';

import {
  type EntitlementConflictViolatingEntitlement,
  type SearchEntitlementConflictsApiResponse,
  type SearchEntitlementConflictsRequest,
  useSearchEntitlementConflictsMutation,
} from '../data-layer';

type FunctionGroupedData = Record<
  string,
  {
    data: EntitlementConflictViolatingEntitlement[];
    searchAfter: string[] | null;
    afterKey: string | null;
    count: number | null;
  }
>;

export const useSearchEntitlementConflicts = () => {
  const [searchEntitlementConflicts, { error, isSuccess }] =
    useSearchEntitlementConflictsMutation();

  const [data, setData] = useState<
    SearchEntitlementConflictsApiResponse | undefined
  >();
  const [functionGroupedData, setFunctionGroupedData] =
    useState<FunctionGroupedData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const handleSearch = useCallback(
    async (params: SearchEntitlementConflictsRequest, isLoadMore = false) => {
      try {
        setIsLoading(true);
        const result = await searchEntitlementConflicts(params).unwrap();
        const response = result as SearchEntitlementConflictsApiResponse;

        // Track function-specific data
        const { functionId } = params;

        setFunctionGroupedData((prevGrouped) => {
          const existingFunctionData = prevGrouped[functionId];

          return {
            ...prevGrouped,
            [functionId]: {
              data:
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                isLoadMore && existingFunctionData
                  ? [...existingFunctionData.data, ...response.data]
                  : response.data,
              searchAfter: response.searchAfter,
              afterKey: response.afterKey,
              count: response.count,
            },
          };
        });

        // Also maintain the combined data for backward compatibility
        setData((prevData) => {
          if (isLoadMore && prevData) {
            return {
              ...response,
              data: [...prevData.data, ...response.data],
            };
          }

          return response;
        });

        setHasMore(
          Boolean(response.searchAfter && response.searchAfter.length > 0)
        );

        return result;
      } catch (err) {
        if (!isLoadMore) {
          setData(undefined);
        }

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [searchEntitlementConflicts]
  );

  const loadMore = useCallback(
    async (
      params: Omit<SearchEntitlementConflictsRequest, 'payload'> & {
        payload: Omit<
          SearchEntitlementConflictsRequest['payload'],
          'searchAfter'
        >;
      }
    ): Promise<void> => {
      if (!data?.searchAfter || !hasMore) {
        return;
      }

      const loadMoreParams = {
        ...params,
        payload: {
          ...params.payload,
          searchAfter: data.searchAfter,
        },
      };

      try {
        await handleSearch(loadMoreParams, true);
      } catch {
        // Handle error silently
      }
    },
    [data?.searchAfter, hasMore, handleSearch]
  );

  const clearData = useCallback(() => {
    setData(undefined);
    setFunctionGroupedData({});
  }, []);

  return {
    searchEntitlementConflicts: handleSearch,
    loadMore,
    data,
    functionGroupedData,
    clearData,
    isLoading,
    hasMore,
    error,
    isSuccess,
    functionCounts: functionGroupedData,
  };
};
